import { Formik } from 'formik';
import React, { useEffect } from 'react';
import { useFormContext } from '../../context/FormContext';
import useForm from '../../hooks/useForm';
import { validateV2, getFields } from '../../utils';
import Banner from '../Banner/banner';
import Footer from '../Footer';
import Form from '../Form';
import { getInitialValues3 } from '../../utils/functions/getInitialValues';
import { makeSchemaV3 } from '../../utils/functions/makeBuildSchema';
import { removeLabelCharacter } from '../../utils/functions/getName';
import { annotateLinkedDropdowns, resolveFieldTriggers, applyVisibilityToSchema } from '../../utils/functions/linkedDropdown';
import { useToast } from '../../hooks/useToast';

const FormContainer = () => {
  const {
    banner_link,
    description,
    name,
    token,
    form_fields,
    setTicketId,
    merchant,
    ...props
  } = useFormContext();

  const { submitData, submitError, submitForm } = useForm();
  const toast = useToast();

  const fields = transformData(form_fields);
  const buildSchema = makeSchemaV3(fields);

  function removeTags(str) {
    if (str === null || str === '') return false;
    else str = str.toString();

    // Regular expression to identify HTML tags in
    // the input string. Replacing the identified
    // HTML tag with a null string.
    return str.replace(/(<([^>]+)>)/gi, '');
  }

  const schema = buildSchema();

  const data = {
    queue_id: Number(props.queues?.[0]?.id),
    queue_type_id: Number(props.queueTypes?.[0]?.id),
    summary: description,
    description: removeTags(description),
    merchant_name: props.merchant_name,
    form_share_link: window.location.href,
    initails_email_response: removeTags(props.initails_email_response),
    is_internal: props.type === "internal",
    web_form_id: props.id,
    mda: props?.mdaRequester,
    channel: 'webform',
  };

  const onSubmit = async (values, { setSubmitting, ...formik }) => {
    function transformValues2(data) {
      return data.map((item) => {
        if (item.name.includes('_')) {
          let name = item.name.split('_');
          item.name = name.length > 1 ? name.slice(0, -1).join('_') : name[0];
          if (item?.label) {
            item.name = item.label?.replaceAll('_', ' ') || item.name;
          }
          if (item?.type?.includes('_')) {
            let aType = item.type.split('_');
            item.type =
              aType.length > 1 ? aType.slice(0, -1).join('_') : aType[0];
          }
          if (item.values) {
            item.values = item.values.map((value) => {
              const nameParts = value.name.split('_');
              if (nameParts.length > 2) {
                value.name = nameParts.slice(0, -1).join('_');
              }
              return value;
            });
          }
        }
        delete item.label;
        return item;
      });
    }
    function transformValues(data) {
      return data.map((item) => {
        // Replace underscores with spaces in top-level name field
        if (item.name.includes('_')) {
          item.name = item.name.replaceAll('_', ' ');
        }

        // Handle type field if it exists and contains underscores
        if (item?.type?.includes('_')) {
          let aType = item.type.split('_');
          item.type =
            aType.length > 1 ? aType.slice(0, -1).join('_') : aType[0];
        }

        // Handle values array if it exists, but do not modify name fields inside
        if (item.values) {
          item.values = item.values.map((value) => {
            // No transformation for value.name to preserve underscores
            return value;
          });
        }

        // Use label to override name if label exists, then delete label
        if (item?.label) {
          item.name = item.label.replaceAll('_', ' ') || item.name;
          delete item.label;
        }

        return item;
      });
    }
 
    try {
      const fields = getFields(values);
      const formValues = {
        ...data,
        fields: transformValues(fields.fields),
      };

      await submitForm({
        files: fields.files,
        token,
        values: formValues,
        options: { setSubmitting },
        type: props.type,
        accessToken: props.accessToken,
        selectedOption: props.selectedOption,
        merchant,
      });
      if (submitData) {
        formik.resetForm();
        toast.show({
          title: "Success",
          description: "Form submitted successfully",
          color: "success"
        });
      }
    } catch (error) {
      console.log(error);
      toast.show({
        title: "Error",
        description: "An unexpected error occurred",
        color: "error"
      });
    }
  };

  useEffect(() => {
    if (submitData) {
      setTicketId(submitData.data.ticket_id);
    }
  }, [submitData]);

  useEffect(() => {
    if (submitError) {
      toast.show({
        title: "Submission Error",
        description: submitError,
        color: "error"
      });
    }
  }, [submitError, toast]);

  function transformData(data) {
    const labelCounts = {};
    const typeCounts = {};

    function getUniqueLabel(label, suffix = '') {
      if (label) {
        label = removeLabelCharacter(label);
        if (labelCounts[label] !== undefined) {
          labelCounts[label] += 1;
          return suffix
            ? `${label}${suffix}`
            : `${label}_${labelCounts[label]}${suffix}`;
        } else {
          labelCounts[label] = 0;
          return `${label}${suffix}`;
        }
      }
    }
    function getUniqueType(label, suffix = '') {
      label = removeLabelCharacter(label);
      if (typeCounts[label] !== undefined) {
        typeCounts[label] += 1;
        return `${label}_${typeCounts[label]}${suffix}`;
      } else {
        typeCounts[label] = 0;
        return `${label}${suffix}`;
      }
    }

    const mapped = data.map((item) => {
      const newItem = { ...item };
      newItem.displayLabel = item.field_label;
      newItem.field_label = getUniqueLabel(
        newItem?.field_key || newItem.field_label
      );
      newItem.field_type = getUniqueType(newItem.field_type);

      if (newItem.field_items && labelCounts[item.field_label] > 0) {
        newItem.field_items = newItem.field_items.map((subItem, index) => {
          if (typeof subItem === "string") {
            return subItem;
          }
          const newSubItem = { ...subItem };
          newSubItem.displayLabel = subItem?.field_label;

          newSubItem.field_label = getUniqueLabel(
            subItem?.field_key || subItem.field_label,
            `_${labelCounts[item?.field_key || item.field_label]}`
          );
          return newSubItem;
        });
      }
      return newItem;
    });
    resolveFieldTriggers(data, mapped);
    return annotateLinkedDropdowns(mapped);
  }

  // console.log({ ...getInitialValues3(fields) },{t:fields});

  return (
    <div className="main-container">
      <Banner name={name} description={description} bannerImage={banner_link} />
      <div className="sub-container">
        {' '}
        <Formik
          initialValues={{ ...getInitialValues3(fields) }}
          validate={(values) => validateV2(values, applyVisibilityToSchema(fields, schema, values))}
          onSubmit={onSubmit}
        >
          <Form fields={fields} />
        </Formik>
      </div>
      <Footer />
    </div>
  );
};

export default FormContainer;
